import React from 'react';
// UI Components
import { Badge, Flex, Text, VisuallyHidden } from '@/components/ui';
import { IconAlertTriangle, IconCheck, IconWand } from '@tabler/icons-react';

// New selector component
import { FieldColumnSelector } from './FieldColumnSelector';

// Types
import { ColumnMapping, MetadataType } from './types';

// Local extended metadata type with priority
interface MetadataTypeWithPriority extends MetadataType {
  priority: number;
}

interface RequiredMetadataChipsProps {
  fileId: string;
  columnMappings: ColumnMapping[];
  onMetadataSelect: (metadataType: string, metadataLabel: string) => void;
  className?: string;
  isSubform?: boolean; // Whether the mapped form is a subform
  hoveredElement?: {
    type: 'column' | 'field' | 'metadata';
    name: string;
  } | null;
  onElementHover?: (
    element: { type: 'column' | 'field' | 'metadata'; name: string } | null
  ) => void;
  getElementOpacity?: (
    elementType: 'column' | 'field' | 'metadata',
    elementName: string
  ) => number;
  // New props for enhanced selectors
  useNewSelector?: boolean;
  fileColumns?: string[];
  onFieldColumnMappingChange?: (
    fileId: string,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    targetField?: string
  ) => void;
}

// Define all available metadata types (same as in FormMappingCard)
const ALL_METADATA_TYPES: MetadataTypeWithPriority[] = [
  {
    type: 'externalSubjectId',
    label: 'External Subject ID',
    description: 'Required: External patient reference',
    priority: 100,
  },
  {
    type: 'subjectNumber',
    label: 'Subject Number',
    description: 'Required: Internal subject numbering',
    priority: 100,
  },
  {
    type: 'siteNumber',
    label: 'Site Number',
    description: 'Required: Numeric site identifier',
    priority: 100,
  },
  {
    type: 'formId',
    label: 'Form ID',
    description: 'Required: Form identifier',
    priority: 100,
  },
  {
    type: 'mainFormId',
    label: 'Main Form ID',
    description: 'Required: Parent form identifier',
    priority: 100,
  },
  {
    type: 'siteName',
    label: 'Site Name',
    description: 'Human-readable site name',
    priority: 80,
  },
  {
    type: 'subjectData',
    label: 'Subject Data',
    description: 'General subject information',
    priority: 60,
  },
  {
    type: 'deletedForm',
    label: 'Deleted Form',
    description: 'Deletion flag',
    priority: 50,
  },
  {
    type: 'formIndex',
    label: 'Form Index',
    description: 'Form ordering',
    priority: 50,
  },
  {
    type: 'site',
    label: 'Site',
    description: 'Site identification',
    priority: 40,
  },
  {
    type: 'trash',
    label: 'Trash',
    description: 'Deleted form flag',
    priority: 30,
  },
];

export const RequiredMetadataChips: React.FC<RequiredMetadataChipsProps> = ({
  fileId,
  columnMappings,
  onMetadataSelect,
  className,
  isSubform = false,
  hoveredElement,
  onElementHover,
  getElementOpacity,
  // New props
  useNewSelector = false,
  fileColumns = [],
  onFieldColumnMappingChange,
}) => {
  void hoveredElement;
  // Get mappings for this file
  const fileMappings = React.useMemo(
    () => columnMappings.filter(mapping => mapping.fileId === fileId),
    [columnMappings, fileId]
  );

  // Always show required metadata types (priority >= 100), but Main Form ID only for subforms
  const requiredMetadataTypes = React.useMemo(
    () =>
      ALL_METADATA_TYPES.filter(metaType => {
        if (metaType.type === 'mainFormId') {
          return isSubform; // Main Form ID is required only for subforms
        }
        return metaType.priority >= 100;
      }),
    [isSubform]
  );

  const optionalMappedMetadataTypes = React.useMemo(
    () =>
      ALL_METADATA_TYPES.filter(
        metaType =>
          metaType.priority < 100 &&
          fileMappings.some(
            mapping =>
              mapping.mappingType === 'metadata' &&
              mapping.metadataType?.type === metaType.type
          )
      ),
    [fileMappings]
  );

  // Combine required and mapped optional metadata
  const displayMetadataTypes = React.useMemo(
    () => [...requiredMetadataTypes, ...optionalMappedMetadataTypes],
    [requiredMetadataTypes, optionalMappedMetadataTypes]
  );

  return (
    <Flex gap="2" wrap="wrap" className={className}>
      <VisuallyHidden>
        <Text size="2xsmall" weight="medium" color="textSecondary">
          Metadata fields:
        </Text>
      </VisuallyHidden>

      {displayMetadataTypes.map(metadataType => {
        // Check if this metadata type is already mapped
        const existingMapping = fileMappings.find(
          mapping =>
            mapping.mappingType === 'metadata' &&
            mapping.metadataType?.type === metadataType.type
        );

        const isMapped = !!existingMapping;
        const isRequired = metadataType.priority >= 100;

        // Check if it was auto-mapped (confidence >= 0.7 for auto, < 0.7 for manual)
        const isAutoMapped =
          existingMapping && (existingMapping.confidence ?? 0) >= 0.7;

        // Check for mapping conflicts - if the mapped column is also used for other metadata types
        const columnConflicts = existingMapping
          ? fileMappings.filter(
              mapping =>
                mapping.columnName === existingMapping.columnName &&
                mapping.mappingType === 'metadata' &&
                mapping.metadataType?.type !== metadataType.type
            )
          : [];

        const hasConflicts = columnConflicts.length > 0;

        // Use new FieldColumnSelector when enabled
        if (
          useNewSelector &&
          fileColumns.length > 0 &&
          onFieldColumnMappingChange
        ) {
          // Create a field-like object for metadata
          const metadataField = {
            dataPoint: metadataType.type,
            label: metadataType.label,
            type: 'metadata' as const,
            required: isRequired,
          };

          return (
            <div
              key={metadataType.type}
              style={{
                transition: 'all 0.2s ease',
                opacity: getElementOpacity
                  ? getElementOpacity('metadata', metadataType.type)
                  : 1,
              }}
              onMouseEnter={() =>
                onElementHover &&
                onElementHover({ type: 'metadata', name: metadataType.type })
              }
              onMouseLeave={() => onElementHover && onElementHover(null)}
            >
              <FieldColumnSelector
                triggerVariant={isRequired && !isMapped ? 'dashed' : 'surface'}
                triggerColor={isRequired && !isMapped ? 'danger' : 'info'}
                field={metadataField}
                fileColumns={fileColumns}
                fileMappings={columnMappings}
                fileId={fileId}
                onMappingChange={onFieldColumnMappingChange}
                triggerLabel={metadataType.label + (hasConflicts ? ' ⚠️' : '')}
              />
            </div>
          );
        }

        // Original badge implementation
        return (
          <Badge
            key={metadataType.type}
            color={isMapped ? 'info' : 'error'}
            variant={isMapped ? 'surface' : 'dashed'}
            size="small"
            startIcon={
              isMapped ? (
                isAutoMapped ? (
                  <IconWand />
                ) : (
                  <IconCheck />
                )
              ) : (
                <IconAlertTriangle />
              )
            }
            onClick={() =>
              onMetadataSelect(metadataType.type, metadataType.label)
            }
            onMouseEnter={() =>
              onElementHover &&
              onElementHover({ type: 'metadata', name: metadataType.type })
            }
            onMouseLeave={() => onElementHover && onElementHover(null)}
            style={{
              transition: 'all 0.2s ease',
              opacity: getElementOpacity
                ? getElementOpacity('metadata', metadataType.type)
                : 1,
            }}
            title={
              isMapped
                ? `${metadataType.label} mapped to column "${existingMapping?.columnName}"${isAutoMapped ? ' (auto-mapped)' : ' (manually mapped)'}${hasConflicts ? ` - Warning: column also mapped to ${columnConflicts.map(c => c.metadataType?.label).join(', ')}` : ''}`
                : `${metadataType.label}: ${metadataType.description}`
            }
          >
            {metadataType.label}
            {hasConflicts && ' ⚠️'}
          </Badge>
        );
      })}
    </Flex>
  );
};

export default RequiredMetadataChips;
